import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { client } from "@/lib/api";
import BreakChainFlow from "@/components/BreakChainFlow";
import MemberInsights from "@/components/MemberInsights";
import { Button } from "@/components/ui/button";
import { Cigarette, Plus, Undo2, Leaf, Target, TimerReset, Flame, Sprout, Unlink } from "lucide-react";

const localToday = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local

function Ring({ count, target }) {
  const over = count > target;
  const denom = target > 0 ? target : Math.max(count, 1);
  const pct = Math.min(count / denom, 1) * 100;
  const R = 52;
  const C = 2 * Math.PI * R;
  const color = over ? "#E0774E" : target === 0 ? "#94A0AE" : "#0C8478";
  return (
    <div className="relative w-44 h-44" data-testid="tracker-ring">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={R} fill="none" stroke="#E4E9ED" strokeWidth="9" />
        <circle cx="60" cy="60" r={R} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)} style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.3s" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-serif text-5xl text-olive leading-none">{count}</span>
        <span className="text-sm text-stone mt-1">of {target} today</span>
      </div>
    </div>
  );
}

export default function TrackerPage() {
  const [overview, setOverview] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [preOpen, setPreOpen] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const r = await client.get(`/taper/overview?date=${localToday()}`);
      const plans = r.data.plans || [];
      setOverview(plans);
      setSelectedId((prev) => prev || (plans[0]?.id ?? null));
      return plans;
    } catch (e) {
      return [];
    }
  }, []);

  const loadTracker = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    const r = await client.get(`/taper/tracker?plan_id=${id}&date=${localToday()}`);
    setData(r.data);
  }, []);

  useEffect(() => { loadOverview().finally(() => setLoading(false)); }, [loadOverview]);
  useEffect(() => { if (selectedId) loadTracker(selectedId); }, [selectedId, loadTracker]);

  const refresh = () => { loadOverview(); if (selectedId) loadTracker(selectedId); };

  const selectPlan = (id) => { setReflection(null); setData(null); setSelectedId(id); };

  const logCigarette = async () => {
    if (logging || !selectedId) return;
    setLogging(true);
    setReflection(null);
    try {
      const res = await client.post("/taper/log", { plan_id: selectedId, date: localToday() });
      setReflection(res.data);
      setData((d) => d ? { ...d, today: { ...d.today, count: res.data.count, target: res.data.target, over: res.data.over } } : d);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log that. Please try again.");
    } finally {
      setLogging(false);
    }
  };
  const resisted = async () => {
    if (!selectedId) return;
    try {
      await client.post("/taper/resist", { plan_id: selectedId, date: localToday() });
      setPreOpen(false);
      toast.success("Chain break recorded — you interrupted the pattern.");
      refresh();
    } catch (e) {
      toast.error("Couldn't save that. Please try again.");
    }
  };

  const logAnyway = async () => {
    setPreOpen(false);
    await logCigarette();
  };

  const undo = async () => {
    if (!selectedId) return;
    try {
      await client.delete(`/taper/log?plan_id=${selectedId}&date=${localToday()}`);
      setReflection(null);
      refresh();
      toast.success("Removed your last entry.");
    } catch (e) {
      toast.error("Nothing to undo.");
    }
  };

  if (loading) return <div className="animate-pulse text-stone" data-testid="tracker-loading">Opening your tracker…</div>;

  const Header = (
    <div>
      <p className="uppercase tracking-[0.2em] text-xs text-terracotta mb-2">Evidence of change</p>
      <h1 className="font-serif text-display text-olive">Progress</h1>
      <p className="text-olive/70 mt-3 max-w-2xl">
        The proof is in the pattern. Log each one as you go, watch your chain breaks add up, and see how
        today measures against your goal — progress, not perfection.
      </p>
    </div>
  );

  if (!overview.length) {
    return (
      <div className="space-y-8" data-testid="tracker-page">
        {Header}
        <div className="rounded-3xl bg-card border border-border p-8 shadow-soft text-center" data-testid="tracker-no-plan">
          <div className="rounded-2xl bg-sage w-14 h-14 flex items-center justify-center mx-auto mb-5"><Target className="w-6 h-6 text-sky" /></div>
          <h2 className="font-serif text-3xl text-olive mb-2">Create a plan to start tracking</h2>
          <p className="text-olive/70 max-w-md mx-auto mb-6">Your daily goal comes from your taper plan. Set one up and you'll be able to log every time.</p>
          <Link to="/app/plan" data-testid="tracker-create-plan" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sand font-medium transition-colors hover:bg-terracotta-soft">
            <Plus className="w-4 h-4" /> Create my plan
          </Link>
        </div>
      </div>
    );
  }

  const ready = !!data?.plan;
  const today = data?.today;
  const history = data?.history || [];
  const noun = data?.noun || "cigarette";
  const plan = data?.plan;
  const chartData = history.map((h) => ({ ...h }));
  const remaining = ready ? Math.max(0, today.target - today.count) : 0;
  const atZero = ready && today.target === 0;

  return (
    <div className="space-y-8" data-testid="tracker-page">
      {Header}

      {/* Plan switcher (only when more than one plan exists) */}
      {overview.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="substance-tabs">
          {overview.map((p) => {
            const active = p.id === selectedId;
            return (
              <button key={p.id} data-testid={`tab-${p.substance}`} onClick={() => selectPlan(p.id)}
                className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium border transition-colors duration-200 ${active ? "bg-brand-teal text-brand-navy border-brand-teal" : "bg-card text-olive/70 border-border hover:bg-sage"}`}>
                <Cigarette className="w-4 h-4" />
                <span className="capitalize">{p.substance}</span>
                <span className={`text-xs ${p.today.over ? (active ? "text-terracotta-soft" : "text-terracotta") : (active ? "text-sand/60" : "text-stone")}`}>
                  {p.today.count}/{p.today.target}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!ready ? (
        <div className="animate-pulse text-stone" data-testid="tracker-switching">Loading…</div>
      ) : (
        <>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Tap + ring */}
            <div className="lg:col-span-2 rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="tracker-today-card">
              <div className="flex flex-col sm:flex-row items-center gap-8">
                <Ring count={today.count} target={today.target} />
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
                    <Cigarette className="w-4 h-4 text-sky" />
                    <span className="text-sm text-stone">Today's goal · {today.target} {plan.unit}</span>
                  </div>
                  <p className={`font-serif text-3xl mb-4 ${today.over ? "text-terracotta" : "text-olive"}`} data-testid="tracker-status">
                    {today.target === 0
                      ? (today.count === 0 ? "Zero today — you're free." : "Every one you skip is a win.")
                      : today.over
                        ? `${today.count - today.target} over goal — grace, not shame.`
                        : `${remaining} left within today's goal.`}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-3 justify-center sm:justify-start">
                    {!atZero ? (
                      <Button data-testid="tap-log-btn" onClick={logCigarette} disabled={logging}
                        className="w-full sm:w-auto rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2 px-7 py-6 text-base">
                        <Flame className="w-5 h-5" /> {logging ? "…" : "I had one — log it"}
                      </Button>
                    ) : (
                      <Button data-testid="tap-breakchain-btn" onClick={() => setPreOpen(true)} disabled={logging}
                        className="w-full sm:w-auto rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2 px-7 py-6 text-base">
                        <Unlink className="w-5 h-5" /> Break the Chain
                      </Button>
                    )}
                    <button data-testid="tap-undo-btn" onClick={undo} className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm text-olive/70 hover:bg-sage transition-colors">
                      <Undo2 className="w-4 h-4" /> Undo
                    </button>
                  </div>
                  {!atZero ? (
                    <button data-testid="open-breakchain-link" onClick={() => setPreOpen(true)}
                      className="inline-flex items-center gap-1.5 text-sm text-terracotta mt-3 hover:underline w-full justify-center sm:justify-start">
                      <Unlink className="w-3.5 h-3.5" /> Feeling an urge? Break the Chain instead
                    </button>
                  ) : (
                    <button data-testid="tap-log-slip" onClick={logCigarette} disabled={logging}
                      className="inline-flex items-center gap-1.5 text-sm text-olive/60 mt-3 hover:underline w-full justify-center sm:justify-start">
                      I slipped — log it honestly, no shame
                    </button>
                  )}
                  {today.resisted > 0 && (
                    <p className="flex items-center gap-1.5 text-sm text-sky mt-3 justify-center sm:justify-start" data-testid="chain-breaks-today">
                      <Sprout className="w-4 h-4" /> {today.resisted} {today.resisted === 1 ? "chain break" : "chain breaks"} today
                    </p>
                  )}
                </div>
              </div>

              <AnimatePresence>
                {reflection && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="mt-6 rounded-2xl bg-sage p-5 flex gap-4" data-testid="tap-reflection">
                    <div className="rounded-full bg-sky/20 p-2.5 h-fit"><Leaf className="w-5 h-5 text-sky" /></div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-stone mb-1">A word for this moment</p>
                      <p className="font-serif text-lg text-olive leading-relaxed">{reflection.message}</p>
                      <Link to="/app/toolkit" className="inline-flex items-center gap-1.5 text-sm text-terracotta mt-3 hover:underline">
                        <TimerReset className="w-4 h-4" /> Delay the next one — ride the urge
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Encouragement / info */}
            <div className="rounded-3xl bg-brand-navy-light text-brand-ivory border border-brand-gold/20 p-7 shadow-soft flex flex-col justify-between" data-testid="tracker-side">
              <div>
                <p className="uppercase tracking-[0.2em] text-[11px] text-gold mb-3">Take heart</p>
                <p className="font-serif text-2xl leading-relaxed">
                  Every craving you outlast rewires you a little more toward freedom. You don't have to win the whole war today — just this one moment.
                </p>
              </div>
              <p className="font-scripture italic text-scripture text-brand-ivory-muted mt-6">“No temptation has overtaken you except what is common to mankind. And God is faithful.” — 1 Corinthians 10:13</p>
            </div>
          </div>

          {/* 7-day chart */}
          <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="tracker-chart-card">
            <h2 className="font-serif text-2xl text-olive mb-1">This week, day by day</h2>
            <p className="text-sm text-stone mb-5">Your logs (colored) against each day's goal (sage). Under goal is a win.</p>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={2} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fill: "#5B6472", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: "#5B6472", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(0,209,194,0.08)" }}
                    contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E9ED", borderRadius: 12, fontFamily: "Outfit", color: "#14181D" }}
                    formatter={(v, n) => [v, n === "target" ? "Goal" : "You had"]}
                  />
                  <Bar dataKey="target" name="target" fill="#E4E9ED" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="count" name="count" radius={[6, 6, 0, 0]}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.hit ? "#00D1C2" : "#E0774E"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-5 mt-4 text-xs text-stone">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#E4E9ED]" /> Daily goal</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#00D1C2]" /> Within goal</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#E0774E]" /> Over goal</span>
            </div>
          </div>
          <BreakChainFlow
            open={preOpen}
            planId={selectedId}
            substance={plan.substance}
            onResisted={resisted}
            onLogAnyway={logAnyway}
            onClose={() => setPreOpen(false)}
          />

          <MemberInsights planId={selectedId} />
        </>
      )}
    </div>
  );
}
